import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'app/components/Button/ButtonVariant';
import { Event } from '../services/api';
import { generateCurrencySymbol } from 'app/routes/BuildProgram/Pricing/utils';
import { DateTime } from 'luxon';

import flagIcon from '../assets/flag.svg';
import { getCoachAvatar } from '../Facility/FacilityPage';

interface EventsClinicsProps {
  events: Event[];
  showViewAll: boolean | undefined;
  onViewAll?: () => void;
  publicProgramsUrl?: string;
}

const EventsClinics: React.FC<EventsClinicsProps> = ({
  events,
  showViewAll,
  onViewAll,
}) => {
  const { t } = useTranslation();
  const shouldShowViewAll = showViewAll;
  /**
   * The frame clamps the description to two lines and stops there, which is
   * fine beside a picture of the clinic — but it left no way to read the rest.
   * The sessions cards on this page already answer that with Show More, so
   * these do the same.
   */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [truncated, setTruncated] = useState<Set<number>>(new Set());
  const descriptionRefs = useRef<{ [key: number]: HTMLParagraphElement | null }>({});

  const toggleExpanded = (id: number) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const displayedEvents = showViewAll ? events.slice(0, 3) : events;

  useEffect(() => {
    const overflowing = new Set<number>();
    Object.entries(descriptionRefs.current).forEach(([id, el]) => {
      if (el && el.scrollHeight > el.clientHeight + 1) {
        overflowing.add(Number(id));
      }
    });
    setTruncated(overflowing);
    // Re-measured when the list changes, not on every render of it.
  }, [events]);

  const handleRegister = (event: Event) => {
    window.location.href = `/public/programs/${event.id}/view`;
  };

  return (
    <div className="pub-section">
      {showViewAll && (
        <div className="pub-section-header">
          <h2 className="pub-section-heading">{t('Events & Clinics')}</h2>
          {shouldShowViewAll && (
            <a
              href="#"
              className="pub-view-all-link"
              onClick={(e) => {
                e.preventDefault();
                if (onViewAll) {
                  onViewAll();
                }
              }}
            >
              {t('View All')}
            </a>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <div className="pub-empty-state">
          <h3 className="pub-empty-state-title">{t('No Events Available')}</h3>
          <p className="pub-empty-state-description">{t('Check back soon for upcoming events and clinics.')}</p>
        </div>
      ) : (
        <>
          <div className={`pub-events-grid ${showViewAll ? 'pub-events-grid-swipeable' : ''}`}>
            {displayedEvents.map((event) => (
              /*
                The frame stacks this card (43552:83856): the picture across the
                top, then what the clinic is, then what it costs and the way in.
                It used to be a row with the picture beside the text.

                The facility and the coach each get a row, as the frame draws
                them (43552:83865, 43552:83870). The row belongs on a coach's own
                page too: Events & Clinics lists the facility's whole public
                programme list, not only this coach's, so it is the only thing
                saying who is running the clinic being looked at.
              */
              <div key={event.id} className="pub-event-card">
                <div className="pub-event-image-wrapper">
                  <img src={event.image} alt={event.title} className="pub-event-image" />
                </div>
                <div className="pub-event-content">
                  <div className="pub-event-info">
                    <h3 className="pub-event-title">{event.title}</h3>
                    <p className="pub-event-date">
                      {DateTime.fromISO(event.date).toFormat("EEEE, LLLL d, yyyy")}
                    </p>
                    {event.location && (
                      <span className="pub-event-location">
                        <img src={flagIcon} alt="" className="pub-event-location-icon" />
                        {event.location}
                      </span>
                    )}
                    {event.coachName && (
                      <span className="pub-event-coach">
                        {/* The same fallback silhouette the facility's own
                            clinic cards use, for a coach with no picture. */}
                        <img
                          src={getCoachAvatar(event.coachAvatar)}
                          alt=""
                          className="pub-event-coach-avatar"
                        />
                        {event.coachName}
                      </span>
                    )}
                  </div>
                  <div>
                    <p
                      ref={(el) => (descriptionRefs.current[event.id] = el)}
                      className={`pub-event-description${
                        expanded.has(event.id) ? " pub-event-description-open" : ""
                      }`}
                    >
                      {event.description}
                    </p>
                    {(truncated.has(event.id) || expanded.has(event.id)) && (
                      <button
                        className="pub-show-more-btn"
                        onClick={() => toggleExpanded(event.id)}
                      >
                        {expanded.has(event.id) ? t("Show Less") : t("Show More")}
                      </button>
                    )}
                  </div>
                  <hr className="pub-event-divider" />
                  <div className="pub-event-footer">
                    <span className="pub-event-price">{event.isFree ? t('Free') : `${generateCurrencySymbol(event.priceCurrency)}${event.price}`}</span>
                    <Button buttonType="secondary-filled" buttonStyles="pub-event-register-btn" title={t('Register')} onClickFunc={() => handleRegister(event)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventsClinics;
